import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { Card, Spinner } from 'react-bootstrap';
import { AiFillEdit } from 'react-icons/ai';
import {
  useGetPersonalDataMutation,
  useGetSelfPersonalDataMutation,
  usePostFollowMutation,
  useUnFollowMutation,
} from '../../services/api/apiProvider';
import { AppState } from '../../store/store';
import { EditProfileModal } from './components/EditProfileModal';
import {
  reset,
  toggleEditModal,
  toggleModalFollowList,
  updateUserData,
} from './slices/profilePageSlice';
import { ReplaceURLs, getLevelColor } from '../../common/utils/utils';
import { ImageCustom } from '../../common/components/ImageCustom';
import officalLiverIcon from '../../assets/svg/officialliver_icon.svg';
import './ProfilePage.scss';
import { FollowListModal } from '../live-follower-list/FollowListModal';
import { useParams } from 'react-router-dom';

export const ProfilePage = (): ReactElement => {
  const { paramsId } = useParams<{ paramsId: string | undefined }>();

  const { userData, isShowEditModal } = useSelector(
    (state: AppState) => state.profilePageState,
  );
  const [loading, setLoading] = useState<boolean>();
  const idUser = userData?.user.id;
  const [titleFollow, setTitleFollow] = useState<string | undefined>('');
  const [statusFollow, setStatusFollow] = useState(
    userData?.followStatus === 'following',
  );

  const dispatch = useDispatch();
  const [getUserData] = useGetSelfPersonalDataMutation();
  const [getPersonalData] = useGetPersonalDataMutation();
  const [postFollow] = usePostFollowMutation();
  const [unFollow] = useUnFollowMutation();

  useEffect(() => {
    fetchUserData();
    return () => {
      dispatch(reset);
    };
  }, [paramsId, statusFollow]);

  useEffect(() => {
    if (!loading) {
      const modifiedStr = ReplaceURLs(userData?.user.description.value);
      const element = document.getElementById('text-target');
      if (element) {
        if (modifiedStr) {
          element.innerHTML = modifiedStr;
        } else element.innerHTML = '';
      }
    }
  }, [loading, userData]);

  const fetchUserData = async () => {
    setLoading(true);
    if (!paramsId) {
      const data = await getUserData().unwrap();
      if (data) {
        dispatch(updateUserData(data));
        setLoading(false);
      }
    } else {
      const data = await getPersonalData(paramsId).unwrap();
      if (data) {
        dispatch(updateUserData(data));
        setLoading(false);
      }
    }
  };

  const handleEditCLick = () => {
    dispatch(toggleEditModal(true));
  };

  const handleToggleFollowListModal = (event: any) => {
    const text = event.target.innerText;
    setTitleFollow(text);
    dispatch(toggleModalFollowList(true));
  };

  const handleFollow = async () => {
    if (userData) {
      if (statusFollow) {
        await unFollow(userData.user.id).unwrap();
        setStatusFollow(!statusFollow);
      } else {
        await postFollow(userData.user.id).unwrap();
        setStatusFollow(!statusFollow);
      }
    }
  };

  if (loading || !userData) {
    return (
      <div className="block-group__loading">
        <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
      </div>
    );
  }

  return (
    <div className="container">
      <div className="profile">
        <Card className="profile__user-info box-shadow-3">
          <div className="profile__avatar">
            <ImageCustom src={userData.user.picture.uri} />
          </div>

          <div className="profile__name">
            <span
              className="profile__level"
              style={{
                backgroundColor: getLevelColor(userData.user.level),
              }}
            >
              {`Lv.${userData.user.level}`}
            </span>
            <span>{userData.user.loginId}</span>
            {userData.user.isCertifiedLiver && (
              <img className="profile__offical-icon" src={officalLiverIcon} />
            )}
          </div>

          <div className="profile__stats">
            <div className="profile__stats-item">
              <div className="profile__stats-item__number">
                {userData.followerCount}
              </div>
              {paramsId ? (
                <div className="profile__stats-item__text ">フォロワー</div>
              ) : (
                <div
                  onClick={(e: any) => handleToggleFollowListModal(e)}
                  className="profile__stats-item__text profile-follow-link"
                >
                  フォロワー
                </div>
              )}
            </div>
            <div className="profile__stats-item">
              <div className="profile__stats-item__number">
                {userData.followingCount}
              </div>
              {paramsId ? (
                <div className="profile__stats-item__text ">フォロー中</div>
              ) : (
                <div
                  onClick={(e: any) => handleToggleFollowListModal(e)}
                  className="profile__stats-item__text profile-follow-link"
                >
                  フォロー中
                </div>
              )}
            </div>
            <div className="profile__stats-item">
              <div className="profile__stats-item__number">
                {userData.niceCount}
              </div>
              <div className="profile__stats-item__text">いいね</div>
            </div>
          </div>

          <div className="profile__catch-phrase">
            {userData.user.catchPhrase.value}
          </div>
          <div id="text-target" className="profile__description"></div>

          {paramsId ? (
            <div onClick={handleFollow}>
              {statusFollow ? (
                <div className="profile__edit-btn background-follow">
                  フォロー中
                </div>
              ) : (
                <div className="profile__edit-btn">フォロー</div>
              )}
            </div>
          ) : (
            <div className="profile__edit-btn" onClick={handleEditCLick}>
              <AiFillEdit style={{ marginRight: 5, fontSize: 18 }} />
              プロフィールを編集
            </div>
          )}
        </Card>
      </div>

      {isShowEditModal && <EditProfileModal />}

      <ToastContainer position="top-right" autoClose={2000} limit={1} />
      <FollowListModal id={idUser} title={titleFollow} />
    </div>
  );
};
